/**
 * Helpers for windscreen claim excess copy (estimates and policy-based flags).
 * @module claims/windscreenExcessUtils
 */

import type { PolicyLink } from '../services/types'

/**
 * Returns true when the policy appears to include excess-free glass cover (Entsia values vary).
 */
export function hasExcessFreeGlassCover(policy: PolicyLink | null | undefined): boolean {
  const raw = policy?.excessFreeGlass?.trim()
  if (!raw) return false
  const u = raw.toUpperCase()
  return u === 'YES' || u === 'Y' || u === 'ADD' || u.startsWith('YES')
}

/**
 * Formats basic excess for display, with a conservative fallback when unknown.
 */
export function formatBasicExcessDisplay(policy: PolicyLink | null | undefined, fallbackDollars = 500): string {
  const n = policy?.basicExcess
  if (typeof n === 'number' && Number.isFinite(n)) {
    return `$${n.toLocaleString('en-AU')}`
  }
  return `$${fallbackDollars.toLocaleString('en-AU')}`
}

/**
 * Human-readable sum insured for excess copy (e.g. water crossing %); falls back to generic phrase.
 */
export function formatSumInsuredSnippet(policy: PolicyLink | null | undefined): string {
  const s = policy?.sumInsured
  if (s == null) return 'your total sum insured'
  if (typeof s === 'number' && Number.isFinite(s)) {
    return `$${s.toLocaleString('en-AU')}`
  }
  const cleaned = String(s).replace(/[^0-9.,]/g, '').replace(/,/g, '')
  const n = parseFloat(cleaned)
  if (Number.isFinite(n)) return `$${n.toLocaleString('en-AU')}`
  return 'your total sum insured'
}
